// Phase 13 — fire an audible cue whenever a task enters its active window.
//
// The cooking screen reclassifies every second (`useNow` → classifyLocal /
// server `/tick`). This hook diffs the set of `active` task ids between
// renders and, for the FIRST task that *newly* became active, plays one
// whistle and then speaks "<course>. <action>" (see sounds.ts/playActivation).
//
// Design decisions baked in here:
//   - One sound for every task (the whistle), then a spoken line whose type
//     word is the lane's course (Alert / Starter / Main / Dessert).
//   - First newly-active task per tick only. When several windows open in the
//     same tick we voice just the earliest (first in phase+time order) and
//     leave the rest visual — but we still record the WHOLE active set as
//     seen, so a skipped task is never voiced late on a subsequent tick.
//   - Spoken once per activation. The hook is edge-triggered (diffs prev vs
//     current active), so a task that stays active is never re-announced.
//
// Two deliberate guards carried over from the cue-only version:
//   - First-run opening cue. On the very first observation we announce only
//     the EARLIEST active task — the opening task of the first timed cook,
//     whether that mode is PreCook, PrepCook or LiveCook — so the cook hears
//     the whistle from the very beginning, then record the rest of the
//     already-active backlog as seen so they stay silent. (Previously this
//     seeded entirely silently, so an opening task already live at t=0 was
//     never voiced; resuming mid-cook with several live tasks still avoids a
//     burst because only the earliest is voiced.)
//   - The active set is tracked even while muted, so toggling sound back on
//     never replays a backlog of announcements for tasks that activated off.
//
// Muting (soundEnabled → false) cancels any in-flight announcement: a spoken
// line can run for seconds, so suppressing only the *next* cue isn't enough.

import { useEffect, useRef } from 'react';
import type { ClassifiedPlan } from '@cookchow/shared';
import { playActivation, stopAnnouncements } from './sounds';
import { useAppStore } from '../state/store';
import { useVoices } from './useVoices';
import { resolvePersona } from './voicePersonas';

export function useTaskActivationSounds(
  classified: ClassifiedPlan | null,
  soundEnabled: boolean,
): void {
  // Previously-seen active ids; `null` until the first observation (seed).
  const prevActive = useRef<Set<string> | null>(null);

  // Speech-speed and voice-persona preferences, resolved against the device's
  // voices. Read through a ref so the announcement effect keeps a steady
  // dependency list and always sees the latest settings without re-running.
  const speechRate = useAppStore((s) => s.speechRate);
  const voicePersona = useAppStore((s) => s.voicePersona);
  const cueSound = useAppStore((s) => s.cueSound);
  const voices = useVoices();
  const speechRef = useRef({ speechRate, voicePersona, cueSound, voices });
  speechRef.current = { speechRate, voicePersona, cueSound, voices };

  // Muting mid-sentence stops the current whistle + speech immediately.
  useEffect(() => {
    if (!soundEnabled) stopAnnouncements();
  }, [soundEnabled]);

  useEffect(() => {
    if (!classified) return;

    // Active tasks in classified order (phase + time), so the first is the
    // earliest-starting one. `firstActive` is the opening task of the run;
    // `newlyActive` are the ones that became active since the last tick.
    const activeIds = new Set<string>();
    const newlyActive: Array<{ id: string; lane: string; action: string }> = [];
    const prev = prevActive.current;
    let firstActive: { lane: string; action: string } | null = null;
    for (const ct of classified.tasks) {
      if (ct.state !== 'active') continue;
      activeIds.add(ct.task.id);
      if (firstActive === null) firstActive = { lane: ct.task.lane, action: ct.task.action };
      if (prev !== null && !prev.has(ct.task.id)) {
        newlyActive.push({ id: ct.task.id, lane: ct.task.lane, action: ct.task.action });
      }
    }

    // Voice one task at the chosen persona / speech-rate / cue.
    const announce = (t: { lane: string; action: string }): void => {
      const {
        speechRate: rate,
        voicePersona: persona,
        cueSound: cue,
        voices: vs,
      } = speechRef.current;
      const { voice, pitch } = resolvePersona(persona, vs);
      playActivation({ lane: t.lane, action: t.action, cue, rate, pitch, voice });
    };

    // First observation: voice the opening task so the cook hears the whistle
    // from the very beginning, then record the rest of the already-active
    // backlog as seen so they stay silent (no burst on entry / resume).
    if (prev === null) {
      if (soundEnabled && firstActive) announce(firstActive);
      prevActive.current = activeIds;
      return;
    }

    // Thereafter: the first newly-active task only; the rest stay visual but
    // are recorded as seen below so they never announce late.
    if (soundEnabled && newlyActive.length > 0) announce(newlyActive[0]!);

    prevActive.current = activeIds;
  }, [classified, soundEnabled]);
}
